import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { FontStyle } from '@inter-ikea-kompis/themes';
import type {
	IDexfSettings,
	IFailedShoppingItem,
	ILocalizedInformation,
	IShoppingItem,
	IShoppingProduct,
	ITheme
} from '@inter-ikea-kompis/types';
import { DexfSettingsUtility, ProductUtility, ShoppingSupport } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';
import type { IKompisElementPropertyValue } from '@inter-ikea-kompis/web-component';

import { AddToList } from '@inter-ikea-kompis/component-add-to-list';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import { Text } from '@ingka/common-styles-webc';
import { checkmark, registerIcon, shoppingBagAdd, warningTriangle } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import type { InlineMessageVariant } from '@ingka/inline-message-webc';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import {
	ModalFooter as SkapaModalFooter,
	ModalHeader as SkapaModalHeader,
	Prompt as SkapaPrompt,
	Sheet as SkapaSheet
} from '@ingka/modal-webc/passive.js';

import AddToBagLayoutEnum from '../../enums/AddToBagLayoutEnum.js';
import AddToBagModalEnum from '../../enums/AddToBagModalEnum.js';
import AddToBagStateEnum from '../../enums/AddToBagStateEnum.js';
import AddToListLayoutEnum from '../../enums/AddToListLayoutEnum.js';
import UnavailableOnlineBehaviourEnum from '../../enums/UnavailableOnlineBehaviourEnum.js';
import AddToBagTracker from '../../trackers/AddToBagTracker.js';
import type IAddToBagAnalyticsProp from '../../types/IAddToBagAnalyticsProp.js';
import type IAddToBagTranslations from '../../types/IAddToBagTranslations.js';
import UnavailableProductsCard from '../unavailable-products-card/UnavailableProductsCard.js';
import AddToBagStyle from './AddToBagStyle.js';

declare global {
	interface HTMLElementTagNameMap {
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'skapa-icon': SkapaIcon;
		'skapa-inline-message': SkapaInlineMessage;
		'skapa-sheet': SkapaSheet;
		'skapa-modal-footer': SkapaModalFooter;
		'skapa-modal-header': SkapaModalHeader;
		'skapa-prompt': SkapaPrompt;
	}
}

/**
 * Add To Bag consists of two predefined buttons making it possible for the user to either add to bag or to shopping list. It also handles confirmation and error handling, as well as the case of when one or more of the products in the planner design are not available online.
 *
 * This is a component mainly used in other components, e.g. Configuration Summary and Product Shelf.
 *
 * @event addToCart Start adding products to shopping cart.
 * @event addToList Start adding products to shopping list.
 * @event addToCartClick User clicks on add to cart button.
 * @event modalClose Modal close
 * @event modalOpen Modal open
 * @event addUnavailableProductsToListChange User clicks the checkbox to add unavailable products to shopping list.
 * @event addUnavailableToCartAndList Start adding unavailable products to shopping cart and shopping list
 * @event unavailableProductsModalContinue Click on continue in the unavailable products modal.
 * @event unavailableProductsCardCancelClick User clicks cancel in the unavailable products modal.
 * @event resetAddToCartState Sent after 3 seconds after addToCartState confirmation to be able to reset the state of the button.
 * @event resetAddToListState Sent after 3 seconds after addToListState confirmation to be able to reset the state of the button.
 *
 * @tracker packages/component-add-to-bag/src/trackers/AddToBagTracker.ts
 */

export default class AddToBag extends KompisElement {
	/**
	 * Theme
	 */
	@property({ type: Object, required: true })
	public theme!: ITheme;

	/**
	 * Localized information. Deprecated - use "translations" and "settings" instead.
	 */
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	/**
	 * The translations retrieved from translations service.
	 */
	@property({ type: Object })
	public translations: IAddToBagTranslations | null = null;

	/**
	 * The settings retrieved from settings service.
	 */
	@property({ type: Object })
	public settings: IDexfSettings | null = null;

	/**
	 * Determines the behaviour of the component when there are unavailable products in the shopping bag.
	 */
	@property({ type: String })
	public unavailableOnlineBehaviour: UnavailableOnlineBehaviourEnum =
		UnavailableOnlineBehaviourEnum.blockPurchase;

	/**
	 * Layout of the add to bag and list buttons.
	 */
	@property({ type: String })
	public layout: AddToBagLayoutEnum = AddToBagLayoutEnum.text;

	/**
	 * Defines the state for add to bag button
	 */
	@property({ type: String })
	public addToCartState: AddToBagStateEnum = AddToBagStateEnum.default;

	/**
	 * Defines the state for add to list button
	 */
	@property({ type: String })
	public addToListState: AddToBagStateEnum = AddToBagStateEnum.default;

	/**
	 * List of items including the quantity.
	 */
	@property({ type: Object, required: true })
	public shoppingProducts!: IShoppingProduct[];

	/**
	 * Disables the buttons.
	 */
	@property({ type: Boolean })
	public disabled = false;

	/**
	 * Modal visible
	 */
	@property({ type: String })
	public visibleModal: AddToBagModalEnum | null = null;

	/**
	 * Selects the checkbox for the UnavailableProductsCard that adds unavailable items when user clicks "continue". Only available if markets supports shopping lists.
	 */
	@property({ type: Boolean })
	public addUnavailableProductsToList = false;

	/**
	 * List of failed shopping products returned when trying to add to cart.
	 */
	@property({ type: Object })
	public failedShoppingItems: IFailedShoppingItem[] | null = null;

	/**
	 * Used for automatic analytics. If null or not set, analytics is turned off.
	 */
	@property({ type: Object })
	public analytics: IAddToBagAnalyticsProp | null = null;

	/**
	 *  Hides the add to list button.
	 */
	@property({ type: Boolean })
	public hideListButton = false;

	private promptElement: SkapaPrompt | null = null;
	private cardElement: UnavailableProductsCard | null = null;
	private triggerElement: HTMLElement | null = null;
	private tracker: AddToBagTracker | null = null;
	private addToCartTimeout: ReturnType<typeof setTimeout> | null = null;
	private addToListTimeout: ReturnType<typeof setTimeout> | null = null;
	private dexfSettings: IDexfSettings | null = null;

	public override render(): RenderResult {
		// We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`
			<style>
				${AddToBagStyle.getStyle({
					theme: this.theme
				})}
			</style>

			${this.getInlineMessage()}
			<div class="buttonContainer">${this.getButtons()}</div>
		`;
	}

	public override async connectedCallback(): Promise<void> {
		registerIcon(checkmark);
		registerIcon(shoppingBagAdd);
		registerIcon(warningTriangle);

		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('skapa-inline-message', SkapaInlineMessage);
		this.customElements.define('skapa-sheet', SkapaSheet);
		this.customElements.define('skapa-modal-footer', SkapaModalFooter);
		this.customElements.define('skapa-modal-header', SkapaModalHeader);
		this.customElements.define('skapa-prompt', SkapaPrompt);
		this.customElements.define('kompis-add-to-list', AddToList);
		this.customElements.define('kompis-unavailable-products-card', UnavailableProductsCard);

		super.connectedCallback();
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.closePrompt();
		this.disconnectTracker();

		if (this.addToCartTimeout) {
			clearTimeout(this.addToCartTimeout);
		}

		if (this.addToListTimeout) {
			clearTimeout(this.addToListTimeout);
		}
	}

	protected override shouldUpdate(
		changedProperties: Map<string, IKompisElementPropertyValue>
	): boolean {
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}

		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}

		// Once the add to bag-button have been set with the state 'confirmation' there should be an event sent after 3 seconds to make it possible to reset its state to 'default' again.
		if (
			changedProperties.has('addToCartState') &&
			this.addToCartState === AddToBagStateEnum.confirmation
		) {
			this.dispatchResetAddToCartState();
		}

		// Once the add to list-button have been set with the state 'confirmation' there should be an event sent after 3 seconds to make it possible to reset its state to 'default' again.
		if (
			changedProperties.has('addToListState') &&
			this.addToListState === AddToBagStateEnum.confirmation
		) {
			this.dispatchResetAddToListState();
		}

		if (changedProperties.has('visibleModal')) {
			if (this.visibleModal === AddToBagModalEnum.unavailableProductCard) {
				this.openPrompt();
			} else {
				this.closePrompt();
			}
		}

		if (changedProperties.has('analytics') && this.hasTranslationsAndSettings()) {
			this.disconnectTracker();
			this.connectTracker();
		}
		this.forwardPropsToCard();

		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return (!!this.translations && !!this.settings) || !!this.localizedInformation;
	}

	private getSettings(): IDexfSettings {
		return <IDexfSettings>this.dexfSettings;
	}

	private getTranslations(): IAddToBagTranslations {
		const translations = this.translations
			? this.translations
			: <IAddToBagTranslations>this.localizedInformation?.translations;

		return translations;
	}

	private dispatchResetAddToCartState(): void {
		if (this.addToCartTimeout) {
			clearTimeout(this.addToCartTimeout);
		}

		this.addToCartTimeout = setTimeout(() => {
			this.dispatchEvent(new CustomEvent('resetAddToCartState', { bubbles: true }));
		}, 3000);
	}

	private dispatchResetAddToListState(): void {
		if (this.addToListTimeout) {
			clearTimeout(this.addToListTimeout);
		}

		this.addToListTimeout = setTimeout(() => {
			this.dispatchEvent(new CustomEvent('resetAddToListState', { bubbles: true }));
		}, 3000);
	}

	private async connectTracker(): Promise<void> {
		if (this.analytics) {
			const reporter = this.analytics.__INTERNAL__reporter;
			if (reporter?.connected === false) {
				// Should use an existing reporter, but wait for it to be connected
				return;
			}

			// Use existing connected reporter, or the tracker should create a new one.
			const reporterId = reporter ? reporter.id : undefined;

			this.tracker = new AddToBagTracker({
				...this.analytics,
				settings: this.getSettings(),
				reporterId
			});
			await this.tracker?.connect();
		}
	}

	private disconnectTracker(): void {
		this.tracker?.disconnect();
		this.tracker = null;
	}

	private closePrompt(): void {
		if (this.promptElement) {
			this.promptElement.open = false;
			this.promptElement = null;
			this.cardElement = null;
		}
	}
	private getButtons(): RenderResult {
		const isOnlineSaleable = this.isOnlineSaleable();

		if (this.layout === AddToBagLayoutEnum.text) {
			return html`${this.getCartButton({ isOnlineSaleable })} ${this.getDivider()}
			${this.getListButton()}`;
		}

		return html`
			${this.getListButton()} ${this.getDivider()} ${this.getCartButton({ isOnlineSaleable })}
		`;
	}

	private getListButton(): RenderResult {
		if (!this.shouldShowAddToList()) {
			return null;
		}

		const layout = this.getAddToListLayout();

		return html`
			<kompis-add-to-list
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.shoppingProducts="${this.shoppingProducts}"
				.analytics="${this.getSubComponentAnalytics()}"
				layout="${layout}"
				state="${this.addToListState}"
				?disabled="${this.disabled || !this.hasPositiveQuantity()}"
				@addToList="${this.onAddToList.bind(this)}"
			>
			</kompis-add-to-list>
		`;
	}

	/**
	 * Get analytics object to pass on to sub components.
	 */
	private getSubComponentAnalytics(): unknown | null {
		const reporterId = this.tracker?.getConnectedReporterId();
		return this.analytics
			? {
					...this.analytics,

					__INTERNAL__reporter: reporterId
						? { connected: true, id: reporterId }
						: { connected: false }
				}
			: null;
	}

	private getAddToListLayout(): AddToListLayoutEnum {
		if (this.layout === AddToBagLayoutEnum.icon) {
			return AddToListLayoutEnum.iconSmall;
		}

		if (ShoppingSupport.hasCartSupport(this.getSettings())) {
			return AddToListLayoutEnum.icon;
		}

		return AddToListLayoutEnum.text;
	}

	private getCartButton(options: { isOnlineSaleable: boolean }): RenderResult {
		if (!ShoppingSupport.hasCartSupport(this.getSettings())) {
			return null;
		}

		const { loading, addToShoppingCartButtonSuccess, addToShoppingBagButton } =
			this.getTranslations();

		const disabled = this.disabled || !options.isOnlineSaleable || !this.hasPositiveQuantity();
		const isLoading = !disabled && this.addToCartState === AddToBagStateEnum.loading;
		const success = this.addToCartState === AddToBagStateEnum.confirmation;
		const label = success ? addToShoppingCartButtonSuccess : addToShoppingBagButton;
		const emphasisedVariant: ButtonVariant = 'emphasised';
		const icon =
			!disabled && this.addToCartState === AddToBagStateEnum.confirmation ? 'checkmark' : null;

		if (this.layout === AddToBagLayoutEnum.icon) {
			// Potential accessibility issue? Buttons should always have some sort of label or description
			return html`
				<skapa-icon-button
					?loading="${isLoading}"
					loadingSrText="${loading}"
					size="small"
					variant="${emphasisedVariant}"
					?disabled="${disabled}"
					@click="${this.onAddToCart.bind(this)}"
				>
					${label}
					<skapa-icon slot="icon" icon="${icon || 'shopping-bag-add'}"></skapa-icon>
				</skapa-icon-button>
			`;
		}

		return html`
			<skapa-button
				variant="${emphasisedVariant}"
				?loading="${isLoading}"
				loadingSrText="${loading}"
				?disabled="${disabled}"
				fluid
				@click="${this.onAddToCart.bind(this)}"
			>
				${icon ? html`<skapa-icon slot="icon" icon="${icon}"></skapa-icon>` : null} ${label}
			</skapa-button>
		`;
	}

	private shouldShowAddToList(): boolean {
		return ShoppingSupport.hasListSupport(this.getSettings()) && !this.hideListButton;
	}

	private getDivider(): RenderResult {
		if (!ShoppingSupport.hasCartSupport(this.getSettings()) || !this.shouldShowAddToList()) {
			return null;
		}

		return html` <div class="divider"></div> `;
	}

	private setTriggerElement(target: EventTarget | null): Element | null {
		if (target instanceof Element) {
			this.triggerElement = this.getTriggerElement();
		}

		return this.triggerElement;
	}

	private getTriggerElement(): HTMLButtonElement {
		return <HTMLButtonElement>(
			(this.shadowRoot
				.querySelector(this.customElements.getInternalName('skapa-button'))
				?.shadowRoot?.querySelector('button') ||
				this.shadowRoot
					.querySelector(this.customElements.getInternalName('skapa-icon-button'))
					?.shadowRoot?.querySelector('button'))
		);
	}

	private onAddToCart(event: MouseEvent | KeyboardEvent): void {
		const triggerElement = this.setTriggerElement(event.currentTarget);
		const shoppingItems = ProductUtility.getShoppingItems(this.getValidShoppingProducts());

		this.dispatchEvent(
			new CustomEvent('addToCartClick', {
				bubbles: true,
				detail: {
					shoppingItems,
					triggerElement
				}
			})
		);

		this.tracker?.sendAddToCartClick();

		if (this.includesUnavailableProducts()) {
			this.dispatchEvent(
				new CustomEvent('modalOpen', {
					bubbles: true,
					detail: {
						visibleModal: AddToBagModalEnum.unavailableProductCard,
						shoppingItems,
						triggerElement
					}
				})
			);

			if (ShoppingSupport.hasListSupport(this.getSettings())) {
				this.dispatchEvent(
					new CustomEvent('addUnavailableProductsToListChange', {
						bubbles: true,
						detail: {
							addUnavailableProductsToList: true
						}
					})
				);
			}
		} else {
			this.dispatchEvent(
				new CustomEvent('addToCart', {
					bubbles: true,
					detail: {
						shoppingItems,
						completeConfiguration: true
					}
				})
			);
		}
	}

	private onAddToList(event: CustomEvent): void {
		this.dispatchEvent(
			new CustomEvent('addToList', {
				bubbles: true,
				detail: {
					...event.detail
				}
			})
		);
		this.triggerElement = event.detail.triggerElement;
	}

	private getInlineMessage(): RenderResult {
		return html`<div aria-live="assertive">${this.getCartUnavailableDisclaimer()}</div>`;
	}

	private getCartUnavailableDisclaimer(): RenderResult {
		if (
			this.isOnlineSaleable() ||
			!ShoppingSupport.hasCartSupport(this.getSettings()) ||
			(this.unavailableOnlineBehaviour === UnavailableOnlineBehaviourEnum.blockPurchase &&
				this.layout === AddToBagLayoutEnum.icon)
		) {
			return null;
		}

		const { addToShoppingCartUnavailable, addToShoppingCartUnavailableShort } =
			this.getTranslations();

		const text =
			this.layout === AddToBagLayoutEnum.text
				? addToShoppingCartUnavailable
				: addToShoppingCartUnavailableShort;

		const cautionaryVariant: InlineMessageVariant = 'cautionary';

		return html`
			<div class="shoppingCartUnavailableMessage">
				<skapa-inline-message
					data-testid="shoppingCartUnavailableMessage"
					subtle
					class="message"
					variant="${cautionaryVariant}"
				>
					<skapa-icon slot="message-icon" icon="warning-triangle"></skapa-icon>
					<span slot="body">${text}</span>
				</skapa-inline-message>
			</div>
		`;
	}

	private getValidShoppingProducts(): IShoppingProduct[] {
		return this.shoppingProducts
			.filter((shoppingProduct) => shoppingProduct.quantity > 0)
			.filter((shoppingProduct) => shoppingProduct.product.content);
	}

	private getUnavailableShoppingProducts(): IShoppingProduct[] {
		return this.shoppingProducts
			.filter((shoppingProduct) => shoppingProduct.quantity > 0)
			.filter((shoppingProduct) => !this.isShoppingProductOnlineSaleable(shoppingProduct));
	}

	private isShoppingProductOnlineSaleable(shoppingProduct: IShoppingProduct): boolean {
		const disableOnlineSaleableVerification =
			this.getSettings().kompis.localization.disableOnlineSaleableVerification;

		return Boolean(
			shoppingProduct.product.content?.priceInformation?.availableForHomeDelivery ||
				shoppingProduct.product.content?.priceInformation?.availableForClickCollect ||
				disableOnlineSaleableVerification
		);
	}

	private isOnlineSaleable(): boolean {
		const disableOnlineSaleableVerification =
			this.getSettings().kompis.localization.disableOnlineSaleableVerification;

		if (
			disableOnlineSaleableVerification ||
			this.unavailableOnlineBehaviour === UnavailableOnlineBehaviourEnum.continueByShoppingList
		) {
			return true;
		}

		return !this.includesUnavailableProducts();
	}

	private includesUnavailableProducts(): boolean {
		return this.shoppingProducts
			.filter((shoppingProduct) => shoppingProduct.quantity > 0)
			.some((shoppingProduct) => !this.isShoppingProductOnlineSaleable(shoppingProduct));
	}

	private hasPositiveQuantity(): boolean {
		return this.shoppingProducts.some((shoppingProduct) => shoppingProduct.quantity > 0);
	}

	private openPrompt(): void {
		if (this.promptElement) {
			return;
		}

		const promptElement = this.createPromptElement();
		this.promptElement = promptElement;

		document.body.appendChild(promptElement);
		promptElement.open = true;
	}

	private forwardPropsToCard(): void {
		if (this.cardElement) {
			this.cardElement.theme = this.theme;
			this.cardElement.shoppingProducts = this.shoppingProducts;
			this.cardElement.addToShoppingList = this.addUnavailableProductsToList;
			this.cardElement.localizedInformation = this.localizedInformation;
			this.cardElement.settings = this.settings;
			this.cardElement.translations = this.translations;
			this.cardElement.failedShoppingItems = this.failedShoppingItems;
		}
	}

	private createPromptElement(): SkapaPrompt {
		const promptElement = this.createPrompt();
		const heading = this.createPromptHeading();
		const cardElement = this.createCard();
		const promptFooterElement = this.createPromptFooter();

		promptElement.appendChild(heading);
		promptElement.appendChild(cardElement);
		promptElement.appendChild(promptFooterElement);
		return promptElement;
	}

	private createPrompt(): SkapaPrompt {
		const promptElement = this.createInternalElement('skapa-prompt');

		promptElement.setAttribute(
			'aria-label',
			this.getTranslations().addToShoppingCartUnavailableDialogueHeader
		);
		promptElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
		promptElement.style.zIndex = '500';

		promptElement.addEventListener('closerequest', () => {
			if (this.promptElement) {
				this.dispatchEvent(
					new CustomEvent('modalClose', {
						bubbles: true,
						detail: {
							visibleModal: null
						}
					})
				);
			}
		});

		promptElement.addEventListener('closed', () => {
			promptElement.parentElement?.removeChild(promptElement);
		});

		return promptElement;
	}

	private createPromptHeading(): HTMLHeadingElement {
		const heading = document.createElement('h2');

		heading.slot = 'title';
		heading.innerText = this.getTranslations().addToShoppingCartUnavailableDialogueHeader;
		heading.className = Text.classnames({ heading: { m: true } });
		heading.style.margin = '0';

		const style = document.createElement('style');
		style.textContent = Text.CSS;

		heading.appendChild(style);

		return heading;
	}

	private createCard(): UnavailableProductsCard {
		this.cardElement = this.createInternalElement('kompis-unavailable-products-card');
		this.forwardPropsToCard();
		this.cardElement.addEventListener('addToShoppingList', (event) => {
			this.dispatchEvent(
				new CustomEvent('addUnavailableProductsToListChange', {
					bubbles: true,
					detail: {
						addUnavailableProductsToList: (event as CustomEvent).detail.addToShoppingList
					}
				})
			);
		});

		return this.cardElement;
	}

	private getShoppingItems(): {
		shoppingListItems: IShoppingItem[];
		shoppingCartItems: IShoppingItem[];
	} {
		const failedShoppingItemNos = this.failedShoppingItems?.map(({ itemNo }) => itemNo) ?? [];
		const shoppingItems = ProductUtility.getShoppingItems(
			this.getValidShoppingProducts()
				.filter(this.isShoppingProductOnlineSaleable.bind(this))
				.filter((sp) => !failedShoppingItemNos.includes(sp.product.content?.ruItemNo ?? ''))
		);
		const failedShoppingProducts = this.shoppingProducts.filter((sp) =>
			failedShoppingItemNos.includes(sp.product.content?.ruItemNo ?? '')
		);
		const shoppingListItems = ProductUtility.getShoppingItems([
			...this.getUnavailableShoppingProducts(),
			...failedShoppingProducts
		]);

		return { shoppingListItems, shoppingCartItems: shoppingItems };
	}

	private createPromptFooter(): SkapaModalFooter {
		const { buttonCancel, buttonContinue } = this.getTranslations();
		const promptFooterElement = this.createInternalElement('skapa-modal-footer');

		promptFooterElement.slot = 'footer';
		promptFooterElement.innerText = buttonContinue;

		const secondaryButtonSpan = document.createElement('span');
		secondaryButtonSpan.slot = 'secondary';
		secondaryButtonSpan.innerText = buttonCancel;

		promptFooterElement.appendChild(secondaryButtonSpan);

		promptFooterElement.addEventListener('primary', () => {
			this.dispatchEvent(
				new CustomEvent('modalClose', {
					bubbles: true,
					detail: {
						visibleModal: null,
						triggerElement: this.triggerElement
					}
				})
			);

			const { shoppingCartItems, shoppingListItems } = this.getShoppingItems();

			this.dispatchEvent(
				new CustomEvent('unavailableProductsModalContinue', {
					bubbles: true,
					detail: {
						shoppingItems: shoppingCartItems
					}
				})
			);
			this.tracker?.sendHandleUnavailableItems(
				shoppingListItems,
				this.addUnavailableProductsToList,
				'continue'
			);

			if (this.addUnavailableProductsToList) {
				const detail = {
					shoppingCartItems,
					shoppingListItems
				};

				this.dispatchEvent(
					new CustomEvent('addUnavailableToCartAndList', { bubbles: true, detail })
				);
			} else {
				this.dispatchEvent(
					new CustomEvent('addToCart', {
						bubbles: true,
						detail: {
							shoppingItems: shoppingCartItems,
							triggerElement: this.getTriggerElement()
						}
					})
				);
			}
		});

		promptFooterElement.addEventListener('secondary', () => {
			this.dispatchEvent(
				new CustomEvent('modalClose', {
					bubbles: true,
					detail: {
						visibleModal: null,
						triggerElement: this.triggerElement
					}
				})
			);

			this.dispatchEvent(
				new CustomEvent('unavailableProductsCardCancelClick', {
					bubbles: true
				})
			);

			const { shoppingListItems } = this.getShoppingItems();
			this.tracker?.sendHandleUnavailableItems(
				shoppingListItems,
				this.addUnavailableProductsToList,
				'cancel'
			);
		});

		return promptFooterElement;
	}
}
declare global {
	interface HTMLElementTagNameMap {
		'kompis-add-to-bag': AddToBag;
	}
}
